import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { ApiTableService } from '../../../../../core/services/api-table.service';
import { ProviderParking } from '../../../../../core/models/provider-parking/provider-parking.model';
import { optionResponse } from '../../../../../core/models/form.model';
import { ApiProviderParkingService } from '../../../../../core/services/api/provider-parking/api-provider-parking.service';
import { ModalProviderParkingComponent } from '../../../modals/provider-parking/modal-provider-parking/modal-provider-parking.component';

@Component({
  selector: 'app-provider-parkings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalProviderParkingComponent
  ],
  templateUrl: './provider-parkings.component.html',
  styleUrl: './provider-parkings.component.css'
})
export class ProviderParkingsComponent implements OnInit {
  providers: ProviderParking[] = [];
  parkingSiteList: optionResponse | null = null;
  isLoading: boolean = false;
  protected Math = Math;

  // --- Search & Filter State ---
  searchText: string = '';
  selectedSite: number = 0; // 0 = all
  isFilterDropdownOpen: boolean = false;

  // Dashboard Stats
  dashboardStats: any[] = [];

  // --- Modal State ---
  isModalOpen: boolean = false;
  selectedProvider: ProviderParking | null = null;

  // --- Pagination ---
  currentPage: number = 1;
  pageSize: number = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems: number = 0;

  // --- Sorting ---
  sortColumn: string = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private router: Router,
    private toast: ToastService,
    private apiProviderParkingService: ApiProviderParkingService,
    private apiTableService: ApiTableService
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.getProviders();
    this.getParkingSiteList();
  }

  /**
   * Fetch all providers and calculate summary
   */
  public getProviders(): void {
    this.isLoading = true;
    this.apiProviderParkingService.getProviderParking().subscribe({
      next: (res: any) => {
        try {
          if (res && Array.isArray(res)) {
            // Casting to ProviderParking if fields exist in response
            this.providers = res;
            this.totalItems = this.providers.length;
            this.calculateDashboardStats();
          } else {
            this.providers = [];
            this.totalItems = 0;
            this.calculateDashboardStats();
          }
        } catch (error) {
          console.error('[Data Processing Error]:', error);
          this.toast.error('โครงสร้างข้อมูลผู้ให้บริการไม่ถูกต้อง');
        } finally {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('[API Error]:', err);
        this.toast.error('การดึงข้อมูลผู้ให้บริการล้มเหลว');
        this.isLoading = false;
      }
    });
  }

  /**
   * Fetch parking sites for dropdown filter
   */
  public getParkingSiteList(): void {
    this.apiTableService.getParkingSiteList().subscribe({
      next: (res) => {
        if (res && res.data.length > 0) {
          this.parkingSiteList = res;
        }
      },
      error: (err) => {
        console.error('[API Error] Fetching SiteList:', err);
      }
    });
  }

  /**
   * Calculate summary stats for providers
   */
  private calculateDashboardStats(): void {
    const total = this.providers.length;
    const withVat = this.providers.filter(p => p.vat && p.vat > 0).length;
    const withTaxId = this.providers.filter(p => p.taxId && p.taxId.trim() !== '').length;
    const uniqueSites = new Set(this.providers.map(p => p.site)).size;

    this.dashboardStats = [
      { title: 'ผู้ให้บริการทั้งหมด', value: total.toLocaleString(), icon: 'business', color: 'text-blue-600' },
      { title: 'จดทะเบียน VAT', value: withVat.toLocaleString(), icon: 'receipt_long', color: 'text-emerald-500' },
      { title: 'มีเลขประจำตัวผู้เสียภาษี', value: withTaxId.toLocaleString(), icon: 'badge', color: 'text-amber-500' },
      { title: 'จำนวนโครงการ (Sites)', value: uniqueSites.toLocaleString(), icon: 'place', color: 'text-indigo-500' }
    ];
  }

  /**
   * Local Search and Filter
   */
  get filteredAndPaginatedData(): ProviderParking[] {
    let filtered = this.providers;

    // Search filter
    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(p =>
        (p.name?.toLowerCase().includes(search)) ||
        (p.address?.toLowerCase().includes(search)) ||
        (p.taxId?.toLowerCase().includes(search)) ||
        (p.shortName?.toLowerCase().includes(search))
      );
    }

    // Site filter
    if (this.selectedSite !== 0) {
      const siteName = this.parkingSiteList?.data.find(s => s.id === this.selectedSite)?.name;
      if (siteName) {
        filtered = filtered.filter(p => p.site === siteName);
      }
    }

    // --- Sorting ---
    if (this.sortColumn) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.totalItems = filtered.length;

    // Pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  // --- UI Helpers ---
  public onSearch(): void {
    this.currentPage = 1;
  }

  public onRefresh(): void {
    this.searchText = '';
    this.selectedSite = 0;
    this.currentPage = 1;
    this.getProviders();
  }

  public navigateToCreate(): void {
    this.selectedProvider = null;
    this.isModalOpen = true;
  }

  public openEditModal(provider: ProviderParking): void {
    this.selectedProvider = provider;
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedProvider = null;
  }

  public onSaveSuccess(): void {
    this.getProviders();
  }

  public toggleFilterDropdown(): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  public toggleSiteFilter(siteId: number): void {
    this.selectedSite = (this.selectedSite === siteId) ? 0 : siteId;
    this.currentPage = 1;
    this.isFilterDropdownOpen = false;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  // --- Pagination Helpers ---
  get totalPages(): number {
    return Math.ceil(this.totalItems / (this.pageSize || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
  }

  public trackByProviderId(index: number, item: ProviderParking): number {
    return item.id;
  }
}
